import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS, ROLE_PERMISSIONS } from '../utils/permissions';
import { usersService } from '../services/api';
import {
  Settings, Moon, Sun, Bell, Shield, User, CheckSquare, Square, 
  AlertCircle, Save, Globe, Smartphone, Mail, Database, Lock, 
  CheckCircle, Key, Activity, QrCode, Download, Trash2, Eye, EyeOff, Copy, Users, RotateCcw
} from 'lucide-react';

const SettingsPage = () => {
  const { user } = useAuth();

  const { isAdmin } = usePermissions();
  
  const [notifications, setNotifications] = useState({
    email: true,
    sms: false,
    alerts: true,
    reports: true,
    lowCredit: true,
    tamper: true,
    inactiveMeter: true,
    highConsumption: true,
    pendingTransaction: true
  });
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [customPermissions, setCustomPermissions] = useState({});
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('permissions');

  // États pour la sécurité
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [showNewApiKey, setShowNewApiKey] = useState(false);
  const [newApiKeyData, setNewApiKeyData] = useState(null);
  const [loadingPermissions, setLoadingPermissions] = useState(false);


  // Définition des permissions groupées depuis PERMISSIONS
  const PERMISSION_GROUPS = {
    '📊 Dashboard': [PERMISSIONS.VIEW_DASHBOARD, PERMISSIONS.VIEW_STATS, PERMISSIONS.VIEW_CHARTS],
    '⚡ Compteurs': [PERMISSIONS.VIEW_METERS, PERMISSIONS.ADD_METER, PERMISSIONS.EDIT_METER, PERMISSIONS.DELETE_METER, PERMISSIONS.BLOCK_METER, PERMISSIONS.CONFIGURE_METER],
    '👥 Clients': [PERMISSIONS.VIEW_CUSTOMERS, PERMISSIONS.ADD_CUSTOMER, PERMISSIONS.EDIT_CUSTOMER, PERMISSIONS.DELETE_CUSTOMER, PERMISSIONS.BLOCK_CUSTOMER],
    '💳 Transactions': [PERMISSIONS.VIEW_TRANSACTIONS, PERMISSIONS.CREATE_SALE, PERMISSIONS.CANCEL_TRANSACTION, PERMISSIONS.REFUND_TRANSACTION],
    '💰 Redevances': [PERMISSIONS.VIEW_FEES, PERMISSIONS.RECORD_PAYMENT,PERMISSIONS.GENERATE_FEES],
    '🔔 Alertes': [PERMISSIONS.VIEW_ALERTS, PERMISSIONS.MARK_ALERT_READ, PERMISSIONS.RESOLVE_ALERT],
    '👤 Utilisateurs': [PERMISSIONS.VIEW_USERS, PERMISSIONS.ADD_USER, PERMISSIONS.EDIT_USER, PERMISSIONS.DELETE_USER],
    '📈 Rapports': [PERMISSIONS.VIEW_REPORTS, PERMISSIONS.EXPORT_REPORTS],
    '⚙️ Système': [PERMISSIONS.CONFIGURE_SYSTEM, PERMISSIONS.CONFIGURE_WAVE, PERMISSIONS.CONFIGURE_SMS],
    '📡 Monitoring': [PERMISSIONS.VIEW_MONITORING, PERMISSIONS.UPDATE_RISK_SCORES, PERMISSIONS.BLOCK_INACTIVE],
    '💰 Comptabilité': [PERMISSIONS.VIEW_ACCOUNTING, PERMISSIONS.MANAGE_EXPENSES],
    '📄 Contrats': [PERMISSIONS.VIEW_CONTRACTS, PERMISSIONS.ADD_CONTRACT, PERMISSIONS.EDIT_CONTRACT, PERMISSIONS.DELETE_CONTRACT],
    '📡 Contrôle à Distance': [PERMISSIONS.VIEW_REMOTE_CONTROL, PERMISSIONS.EXECUTE_REMOTE_ACTIONS], // ✅ NOUVEAU
    '☀️ Centrales Solaires': [PERMISSIONS.VIEW_POWER_PLANTS, PERMISSIONS.MANAGE_POWER_PLANTS], // ✅ AJOUT
  };

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
    fetchSecurityData();
    loadNotificationSettings();
  }, [isAdmin]);

  
  const loadNotificationSettings = async () => {
      try {
        const response = await fetch('https://api.hte.sn/api/settings/me', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        if (!response.ok) {
          console.error('❌ Erreur chargement settings:', response.status);
          return;
        }
        
        const data = await response.json();
        console.log('✅ Settings chargés:', data);
        
        setNotifications({
          email: data.email_notifications ?? true,
          sms: data.sms_notifications ?? false,
          alerts: data.alert_notifications ?? true,
          reports: data.report_notifications ?? true,
          lowCredit: data.low_credit_alerts ?? true,
          tamper: data.tamper_alerts ?? true,
          inactiveMeter: data.inactive_meter_alerts ?? true,
          highConsumption: data.high_consumption_alerts ?? true,
          pendingTransaction: data.pending_transaction_alerts ?? true
        });
      } catch (error) {
        console.error('❌ Erreur chargement paramètres:', error);
      }
    };

const fetchUsers = async () => {
  try {
    const res = await usersService.getAll();
    console.log('📥 Response users:', res.data);
    
    // ✅ CORRECTION : Accéder à items
    const usersArray = res.data?.items || res.data || [];
    
    console.log('👥 Users array:', usersArray);
    console.log('👥 Nombre users:', usersArray.length);
    setUsers(usersArray);
  } catch (error) {
    console.error('❌ Erreur fetchUsers:', error);
    setUsers([]);
  }
};
  const fetchSecurityData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Récupérer le statut 2FA
      const status2FA = await fetch('https://api.hte.sn/api/security/2fa/status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data2FA = await status2FA.json();
      setTwoFactorEnabled(data2FA.enabled);

      // Récupérer les sessions
      const resSessions = await fetch('https://api.hte.sn/api/security/sessions', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const dataSessions = await resSessions.json();
      setSessions(dataSessions);

      // Récupérer les clés API
      const resKeys = await fetch('https://api.hte.sn/api/security/api-keys', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // const dataKeys = await resKeys.json();
      // setApiKeys(dataKeys);
      const dataKeys = await resKeys.json();
setApiKeys(Array.isArray(dataKeys) ? dataKeys : []);
    } catch (error) {
      console.error('Erreur chargement sécurité:', error);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleNotificationChange = async (key) => {
    const newValue = !notifications[key];
    setNotifications({ ...notifications, [key]: newValue });
    setSavingNotifications(true);
    
    try {
      const mapping = {
        email: 'email_notifications',
        sms: 'sms_notifications',
        alerts: 'alert_notifications',
        reports: 'report_notifications',
        lowCredit: 'low_credit_alerts',
        tamper: 'tamper_alerts',
        inactiveMeter: 'inactive_meter_alerts',
        highConsumption: 'high_consumption_alerts',
        pendingTransaction: 'pending_transaction_alerts'
      };
      
      const response = await fetch('https://api.hte.sn/api/settings/me', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [mapping[key]]: newValue })
      });
      
      if (response.ok) {
        showMessage('success', '✅ Préférence mise à jour');
      } else {
        showMessage('error', '❌ Erreur mise à jour');
        setNotifications({ ...notifications, [key]: !newValue });
      }
    } catch (error) {
      showMessage('error', '❌ Erreur réseau');
      setNotifications({ ...notifications, [key]: !newValue });
    } finally {
      setSavingNotifications(false);
    }
  };

 const handleSelectUser = async (userId) => {
  const userToEdit = users.find(u => u.id === userId);
  if (!userToEdit) return;

  setSelectedUser(userToEdit);
  setLoadingPermissions(true);

  try {
    // 1️⃣ Base = permissions par défaut du rôle
    const rolePermissions = ROLE_PERMISSIONS[userToEdit.role] || [];
    const permissionsMap = {};

    Object.values(PERMISSIONS).forEach(perm => {
      permissionsMap[perm] = rolePermissions.includes(perm);
    });

    // 2️⃣ Charger permissions custom depuis le backend
    const response = await usersService.getPermissions(userId);

    if (response.data?.custom_permissions) {
      Object.entries(response.data.custom_permissions).forEach(([perm, value]) => {
        permissionsMap[perm] = value;
      });
    }

    // 3️⃣ Appliquer au state
    setCustomPermissions(permissionsMap);

  } catch (error) {
    console.error('❌ Erreur chargement permissions:', error);

    // fallback : permissions du rôle uniquement
    const fallbackPerms = {};
    const rolePerms = ROLE_PERMISSIONS[userToEdit.role] || [];

    Object.values(PERMISSIONS).forEach(perm => {
      fallbackPerms[perm] = rolePerms.includes(perm);
    });

    setCustomPermissions(fallbackPerms);
  } finally {
    setLoadingPermissions(false);
  }
};

  const togglePermission = (permission) => {
    setCustomPermissions({
      ...customPermissions,
      [permission]: !customPermissions[permission]
    });
  };

   const handleSavePermissions = async () => {
    if (!selectedUser) return;
    setSavingPermissions(true);

    try {
      console.log('💾 Sauvegarde permissions:', customPermissions);
      const response = await usersService.updatePermissions(selectedUser.id, customPermissions);
      console.log('✅ Réponse serveur:', response.data);
      showMessage('success', '✅ Permissions mises à jour avec succès');
      await fetchUsers();
    } catch (error) {
      console.error('❌ Erreur mise à jour:', error);
      showMessage('error', '❌ Erreur mise à jour permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

 const handleResetPermissions = async () => {
    if (!selectedUser) return;
    if (!window.confirm(`Réinitialiser les permissions de ${selectedUser.full_name || selectedUser.username} aux valeurs par défaut de son rôle ?`)) return;

    try {
      await usersService.resetPermissions(selectedUser.id);
      showMessage('success', '✅ Permissions réinitialisées');
      await handleSelectUser(selectedUser.id); // Recharger les permissions
    } catch (error) {
      console.error('❌ Erreur réinitialisation:', error);
      showMessage('error', '❌ Erreur réinitialisation');
    }
  };
   const formatPermissionName = (permission) => {
    return permission
      .split('_')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };
  // Fonctions 2FA
  const handleEnable2FA = async () => {
    try {
      const response = await fetch('https://api.hte.sn/api/security/2fa/enable', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      setQrCodeData(data);
      setBackupCodes(data.backup_codes);
      setShowQRCode(true);
    } catch (error) {
      showMessage('error', 'Erreur activation 2FA');
    }
  };

  const handleVerify2FA = async () => {
    try {
      const response = await fetch('https://api.hte.sn/api/security/2fa/verify', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: verificationCode })
      });
      
      if (response.ok) {
        setTwoFactorEnabled(true);
        setShowQRCode(false);
        showMessage('success', '2FA activé avec succès');
        setVerificationCode('');
      } else {
        showMessage('error', 'Code invalide');
      }
    } catch (error) {
      showMessage('error', 'Erreur vérification');
    }
  };

  const handleDisable2FA = async () => {
    const code = prompt('Entrez votre code 2FA pour désactiver:');
    if (!code) return;
    
    try {
      const response = await fetch('https://api.hte.sn/api/security/2fa/disable', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code })
      });
      
      if (response.ok) {
        setTwoFactorEnabled(false);
        showMessage('success', '2FA désactivé');
      } else {
        showMessage('error', 'Code invalide');
      }
    } catch (error) {
      showMessage('error', 'Erreur désactivation');
    }
  };

  const handleRevokeSession = async (sessionId) => {
    if (!window.confirm('Révoquer cette session ?')) return;
    
    try {
      await fetch(`https://api.hte.sn/api/security/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      showMessage('success', 'Session révoquée');
      fetchSecurityData();
    } catch (error) {
      showMessage('error', 'Erreur révocation');
    }
  };

  const handleCreateApiKey = async () => {
    const name = prompt('Nom de la clé API:');
    if (!name) return;
    
    try {
      const response = await fetch('https://api.hte.sn/api/security/api-keys', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, expires_days: 365 })
      });
      const data = await response.json();
      setNewApiKeyData(data);
      setShowNewApiKey(true);
      fetchSecurityData();
      showMessage('success', 'Clé API créée');
    } catch (error) {
      showMessage('error', 'Erreur création clé');
    }
  };

  const handleRevokeApiKey = async (keyId) => {
    if (!window.confirm('Révoquer cette clé API ?')) return;
    
    try {
      await fetch(`https://api.hte.sn/api/security/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      showMessage('success', 'Clé API révoquée');
      fetchSecurityData();
    } catch (error) {
      showMessage('error', 'Erreur révocation');
    }
  };

  const tabs = [
    { id: 'permissions', label: 'Permissions', icon: Shield, adminOnly: true },
    { id: 'security', label: 'Sécurité', icon: Lock, adminOnly: true },
  ];

  const visibleTabs = tabs.filter(tab => !tab.adminOnly || isAdmin);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white flex items-center gap-4 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Settings className="w-7 h-7 text-white" />
            </div>
            Paramètres
          </h1>
          <p className="text-gray-600 dark:text-gray-400 ml-16">
            Gérez vos préférences et configurations
          </p>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300 border-2 border-green-200' 
              : 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300 border-2 border-red-200'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-6 h-6" /> : <AlertCircle className="w-6 h-6" />}
            <span className="font-semibold text-lg">{message.text}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 mb-6 p-2">
          <div className="flex gap-2 overflow-x-auto">
            {visibleTabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Contenu Permissions - INCHANGÉ */}
        {activeTab === 'permissions' && isAdmin && (
          <div className="grid lg:grid-cols-12 gap-6">
             {/* Sélection utilisateur */}
          <div className="lg:col-span-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-3">
                <Users className="w-6 h-6 text-blue-600" />
                Sélectionner un utilisateur
              </h2>
              
              <select
                value={selectedUser?.id || ''}
                onChange={(e) => handleSelectUser(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
              >
                <option value="">Choisir un utilisateur...</option>
                {/* {users.map(u => ( */}
                {(users || []).map(u => (
                  <option key={u.id} value={u.id}>
                    {u.full_name || u.username} ({u.role})
                  </option>
                ))}
              </select>

                {selectedUser && (
                <div className="mt-6 p-4 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl border-2 border-blue-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 dark:text-white">
                        {selectedUser.full_name || selectedUser.username}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                        Rôle: {selectedUser.role}
                      </p>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    <p>Email: {selectedUser.email}</p>
                    {selectedUser.phone && <p>Tél: {selectedUser.phone}</p>}
                  </div>
                </div>
              )}
            </div>
          </div>

        {/* Permissions */}
          <div className="lg:col-span-8">
            {selectedUser ? (
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                    <Shield className="w-6 h-6 text-blue-600" />
                    Permissions
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={handleResetPermissions}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Réinitialiser
                    </button>
                    <button
                      onClick={handleSavePermissions}
                      disabled={savingPermissions}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 disabled:opacity-50 transition-all shadow-lg hover:shadow-xl"
                    >
                      <Save className="w-5 h-5" />
                      {savingPermissions ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                  </div>
                </div>

                {loadingPermissions ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Chargement des permissions...</p>
                  </div>
                ) : (
                  <div className="space-y-6 max-h-[600px] overflow-y-auto pr-2">
                    {Object.entries(PERMISSION_GROUPS).map(([group, permissions]) => (
                      <div key={group} className="border-b border-gray-200 dark:border-gray-700 pb-6 last:border-0">
                        <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-4">{group}</h3>
                        <div className="grid gap-2">
                          {permissions.map(permission => {
                            const isChecked = customPermissions[permission] === true;
                            return (
                              <button
                                key={permission}
                                onClick={() => togglePermission(permission)}
                                className={`flex items-center gap-3 w-full p-4 rounded-xl text-left transition-all ${
                                  isChecked
                                    ? 'bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-2 border-blue-300 shadow-md'
                                    : 'bg-gray-50 dark:bg-gray-700/50 border-2 border-gray-200 dark:border-gray-600 hover:border-gray-300 hover:shadow-sm'
                                }`}
                              >
                                <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                                  isChecked ? 'bg-blue-600' : 'bg-gray-300'
                                }`}>
                                  {isChecked ? (
                                    <CheckSquare className="w-5 h-5 text-white" />
                                  ) : (
                                    <Square className="w-5 h-5 text-gray-400" />
                                  )}
                                </div>
                                <span className={`text-sm font-medium ${
                                  isChecked ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'
                                }`}>
                                  {formatPermissionName(permission)}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-12 flex flex-col items-center justify-center min-h-[500px]">
                <Shield className="w-16 h-16 text-gray-400 mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                  Gestion des permissions
                </h3>
                <p className="text-gray-500 dark:text-gray-400 text-center">
                  Sélectionnez un utilisateur pour gérer ses permissions
                </p>
              </div>
            )}
          </div>
          </div>
        )}
        {/* Contenu Sécurité */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            {/* 2FA */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Lock className="w-6 h-6 text-red-600" />
                Authentification à deux facteurs (2FA)
              </h2>
              
              <div className="p-6 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 rounded-xl border-2 border-red-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center">
                    <QrCode className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                      2FA {twoFactorEnabled ? 'Activé' : 'Désactivé'}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      {twoFactorEnabled ? 'Votre compte est protégé' : 'Ajoutez une couche de sécurité supplémentaire'}
                    </p>
                    
                    {!twoFactorEnabled && !showQRCode && (
                      <button 
                        onClick={handleEnable2FA}
                        className="px-6 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700"
                      >
                        Activer 2FA
                      </button>
                    )}
                    
                    {twoFactorEnabled && (
                      <button 
                        onClick={handleDisable2FA}
                        className="px-6 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700"
                      >
                        Désactiver 2FA
                      </button>
                    )}
                    
                    {showQRCode && qrCodeData && (
                      <div className="mt-4 space-y-4">
                        <div className="bg-white p-4 rounded-xl">
                          <img src={qrCodeData.qr_code} alt="QR Code" className="mx-auto" />
                          <p className="text-sm text-center mt-2 text-gray-600">
                            Scannez avec Google Authenticator
                          </p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">Code de vérification</label>
                          <input
                            type="text"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="000000"
                            maxLength={6}
                          />
                        </div>
                        
                        <button
                          onClick={handleVerify2FA}
                          className="w-full px-6 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700"
                        >
                          Vérifier et activer
                        </button>
                        
                        {backupCodes.length > 0 && (
                          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-xl">
                            <p className="font-bold mb-2">Codes de secours (gardez-les précieusement) :</p>
                            <div className="grid grid-cols-2 gap-2 text-sm font-mono">
                              {backupCodes.map((code, i) => (
                                <div key={i}>{code}</div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Sessions */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Activity className="w-6 h-6 text-blue-600" />
                {isAdmin ? 'Sessions Actives - Tous les Utilisateurs' : 'Mes Sessions Actives'}
              </h2>
              
              <div className="space-y-3">
                {sessions.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Aucune session active</p>
                ) : (
                  // sessions.map(session => (
                    (sessions || []).map(session => (
                    <div key={session.id} className={`flex items-center justify-between p-4 rounded-xl ${
                      session.is_current 
                        ? 'bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border-2 border-green-300'
                        : 'bg-gray-50 dark:bg-gray-700'
                    }`}>
                      <div className="flex-1">
                        {isAdmin && (
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="w-4 h-4 text-blue-600" />
                            <span className="font-bold text-blue-600">
                              {session.full_name || session.username}
                            </span>
                          </div>
                        )}
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {session.device_info || 'Appareil inconnu'}
                          {session.is_current && (
                            <span className="ml-2 text-xs bg-green-600 text-white px-2 py-1 rounded-full">
                              Session actuelle
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {session.ip_address} • {session.location || 'Localisation inconnue'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Dernière activité: {new Date(session.last_activity).toLocaleString('fr-FR')}
                        </p>
                      </div>
                      {!session.is_current && (
                        <button
                          onClick={() => handleRevokeSession(session.id)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Révoquer
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Clés API */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                  <Key className="w-6 h-6 text-purple-600" />
                  Clés API
                </h2>
                <button
                  onClick={handleCreateApiKey}
                  className="px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 flex items-center gap-2"
                >
                  <Key className="w-4 h-4" />
                  Créer une clé
                </button>
              </div>
              
              {showNewApiKey && newApiKeyData && (
                <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-300 rounded-xl">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <p className="font-bold text-green-800 dark:text-green-300 mb-2">
                        Clé API créée avec succès !
                      </p>
                      <p className="text-sm text-green-700 dark:text-green-400 mb-3">
                        Copiez cette clé maintenant, elle ne sera plus affichée.
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newApiKeyData.key}
                          readOnly
                          className="flex-1 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 rounded-lg font-mono text-sm"
                        />
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(newApiKeyData.key);
                            showMessage('success', 'Clé copiée !');
                          }}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                        >
                          <Copy className="w-4 h-4" />
                          Copier
                        </button>
                      </div>
                      <button
                        onClick={() => setShowNewApiKey(false)}
                        className="mt-3 text-sm text-green-700 dark:text-green-400 underline"
                      >
                        J'ai copié ma clé, fermer
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="space-y-3">
                {apiKeys.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Aucune clé API</p>
                ) : (
                  // apiKeys.map(key => (
                    (apiKeys || []).map(key => (
                    <div key={key.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {key.name}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                          {key.prefix}
                        </p>
                        <div className="flex gap-4 mt-2 text-xs text-gray-500">
                          <span>
                            Créée: {new Date(key.created_at).toLocaleDateString('fr-FR')}
                          </span>
                          {key.last_used_at && (
                            <span>
                              Dernière utilisation: {new Date(key.last_used_at).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                          <span>
                            Expire: {new Date(key.expires_at).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRevokeApiKey(key.id)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Révoquer
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Changement de mot de passe */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Lock className="w-6 h-6 text-indigo-600" />
                Mot de passe
              </h2>
              
              <div className="p-6 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-xl border-2 border-indigo-200">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center">
                    <Lock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                      Modifier le mot de passe
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      Changez votre mot de passe régulièrement pour plus de sécurité
                    </p>
                    <button 
                      onClick={() => {
                        const oldPassword = prompt('Ancien mot de passe:');
                        if (!oldPassword) return;
                        const newPassword = prompt('Nouveau mot de passe (min 6 caractères):');
                        if (!newPassword || newPassword.length < 6) {
                          showMessage('error', 'Mot de passe trop court');
                          return;
                        }
                        
                        fetch('https://api.hte.sn/api/users/change-password', {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({ oldPassword, newPassword })
                        })
                        .then(res => res.json())
                        .then(data => {
                          if (data.message) {
                            showMessage('success', data.message);
                          } else {
                            showMessage('error', data.detail || 'Erreur');
                          }
                        })
                        .catch(() => showMessage('error', 'Erreur réseau'));
                      }}
                      className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
                    >
                      Changer le mot de passe
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;